import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { useApi } from '../../../design-system/useApi';
import { VISUAL } from '../../../design-system/visual';
import { hrApi } from '../api';
import type { AttendanceSettings, BreakPolicy } from '../types';

/** Owner-facing attendance policy — idle auto-pause + break rules. */
export function AttendanceSettingsPanel() {
  const { data, loading, error, status, reload } = useApi<{ settings: AttendanceSettings }>(
    '/api/hr/attendance/settings',
  );

  // Idle threshold is edited in minutes for readability, converted to
  // seconds on save (matches the API's idle_pause_threshold_seconds).
  const [idleMinutes, setIdleMinutes] = React.useState('2');
  const [breakPolicy, setBreakPolicy] = React.useState<BreakPolicy>('anytime');
  const [maxBreakMinutes, setMaxBreakMinutes] = React.useState('');
  const [schedulingEnabled, setSchedulingEnabled] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!data?.settings) return;
    const s = data.settings;
    setIdleMinutes(String(Math.round(s.idle_pause_threshold_seconds / 60)));
    setBreakPolicy(s.break_policy);
    setMaxBreakMinutes(s.max_break_minutes != null ? String(s.max_break_minutes) : '');
    setSchedulingEnabled(s.scheduling_enabled);
  }, [data]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    const minutes = Number(idleMinutes);
    if (!Number.isFinite(minutes) || minutes < 0) {
      toast.error('Idle auto-pause must be a non-negative number of minutes');
      return;
    }
    setSaving(true);
    try {
      const updated = await hrApi.updateAttendanceSettings({
        idle_pause_threshold_seconds: Math.round(minutes * 60),
        break_policy: breakPolicy,
        max_break_minutes: breakPolicy === 'scheduled'
          ? (maxBreakMinutes ? Number(maxBreakMinutes) : null)
          : null,
        scheduling_enabled: schedulingEnabled,
      });
      setIdleMinutes(String(Math.round(updated.idle_pause_threshold_seconds / 60)));
      setBreakPolicy(updated.break_policy);
      setMaxBreakMinutes(updated.max_break_minutes != null ? String(updated.max_break_minutes) : '');
      setSchedulingEnabled(updated.scheduling_enabled);
      toast.success('Attendance policy saved');
    } catch (err: any) {
      toast.error('Could not save', { description: err.message });
    } finally { setSaving(false); }
  }

  if (loading) return <LoadingState variant="inline" />;
  if (status === 403) {
    return (
      <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
        <NoAccessState description="The attendance policy is managed by workspace owners." />
      </section>
    );
  }
  if (error) {
    return (
      <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
        <ErrorState title="Couldn't load the attendance policy" description={error} onRetry={reload} />
      </section>
    );
  }

  return (
    <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
      <h3 className={[VISUAL.dashSection, 'mb-2'].join(' ')}>Attendance policy</h3>
      <p className="text-[12.5px] text-muted-foreground mb-5">
        Idle time and breaks pause the timer, so employees are paid for active time only.
      </p>
      <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="grid gap-1.5">
          <Label>Idle auto-pause after (minutes)</Label>
          <Input
            type="number" min={0} step="1"
            value={idleMinutes}
            onChange={(e) => setIdleMinutes(e.target.value)}
            placeholder="2"
          />
        </div>
        <div className="grid gap-1.5">
          <Label>Break policy</Label>
          <Select value={breakPolicy} onValueChange={(v) => setBreakPolicy(v as BreakPolicy)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="anytime">Anytime</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {breakPolicy === 'scheduled' && (
          <div className="grid gap-1.5">
            <Label>Max break minutes</Label>
            <Input
              type="number" min={0} step="1"
              value={maxBreakMinutes}
              onChange={(e) => setMaxBreakMinutes(e.target.value)}
              placeholder="30"
            />
          </div>
        )}
        <div className="grid gap-1.5 sm:col-span-3">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              aria-label="Shift scheduling"
              checked={schedulingEnabled}
              onChange={(e) => setSchedulingEnabled(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-border accent-saffron"
            />
            <span>
              <span className="text-[13px] font-medium text-foreground block">
                Shift scheduling
              </span>
              <span className="text-[12px] text-muted-foreground block mt-0.5">
                {schedulingEnabled
                  ? 'On: assign shift templates and per-day overrides.'
                  : 'Off: everyone follows their standard hours.'}
              </span>
            </span>
          </label>
        </div>
        <div className="sm:col-span-3 flex justify-end pt-2">
          <Button type="submit" disabled={saving} className="rounded-full h-10 px-5 text-[13px] font-semibold gap-1.5">
            {saving ? 'Saving…' : 'Save policy'}
          </Button>
        </div>
      </form>
    </section>
  );
}
